<template>
  <div>
    <LoaderComponent v-if="loading || paying" />

    <div v-if="pageState === 'ready' && isPaid" class="invoice-thank-you-page">
      <div class="invoice-thank-you-card">
        <div class="invoice-thank-you-logo">
          <img src="@/assets/common/images/lbhq-logo.svg" alt="Link Building HQ" />
        </div>
        <div class="invoice-thank-you-icon" aria-hidden="true">
          <i class="fas fa-check"></i>
        </div>
        <h2 class="invoice-thank-you-title">Thank you</h2>
        <p class="invoice-thank-you-copy">
          Your payment was successful. You can download your invoice below.
        </p>
        <button
          type="button"
          class="invoice-thank-you-download"
          :disabled="loading"
          @click="downloadInvoice"
        >
          <i class="fas fa-download"></i>
          Download Invoice
        </button>
      </div>
    </div>

    <div v-else-if="pageState === 'ready' && isProcessing" class="invoice-thank-you-page">
      <div class="invoice-thank-you-card">
        <div class="invoice-thank-you-logo">
          <img src="@/assets/common/images/lbhq-logo.svg" alt="Link Building HQ" />
        </div>
        <div class="invoice-thank-you-icon is-processing" aria-hidden="true">
          <i class="fas fa-clock"></i>
        </div>
        <h2 class="invoice-thank-you-title">Payment processing</h2>
        <p class="invoice-thank-you-copy">
          Your ACH bank transfer has started. This usually takes 2–5 business days
          to clear. The order is on hold until Stripe confirms the payment.
        </p>
      </div>
    </div>

    <div v-else-if="pageState === 'ready' && isFailed" class="invoice-thank-you-page">
      <div class="invoice-thank-you-card">
        <div class="invoice-thank-you-logo">
          <img src="@/assets/common/images/lbhq-logo.svg" alt="Link Building HQ" />
        </div>
        <div class="invoice-thank-you-icon is-failed" aria-hidden="true">
          <i class="fas fa-times"></i>
        </div>
        <h2 class="invoice-thank-you-title">Payment failed</h2>
        <p class="invoice-thank-you-copy">
          The ACH bank transfer could not be completed. This invoice cannot be paid again from this link.
        </p>
      </div>
    </div>

    <div v-else class="invoice-payment-page">
      <div v-if="pageState === 'invalid'" class="invoice-payment-card invoice-status-card">
        <section class="invoice-checkout-panel">
          <h2 class="invoice-checkout-title">Payment link is invalid</h2>
          <p class="invoice-paypal-copy">
            This checkout URL is missing a valid payment token. Please use the link sent to you.
          </p>
        </section>
      </div>

      <div v-else-if="pageState === 'ready'" class="invoice-payment-card">
      <aside class="invoice-summary-panel">
        <div class="invoice-summary-header">
          <div class="invoice-summary-top">
            <div class="invoice-brand-block">
              <div class="invoice-brand">
                <img src="@/assets/common/images/lbhq-logo.svg" alt="Link Building HQ" />
              </div>
              <span
                class="invoice-status-badge"
                :class="statusBadgeClass"
              >
                {{ statusBadgeLabel }}
              </span>
            </div>

            <div v-if="invoiceNo || issuedAtLabel" class="invoice-info-box">
              <p class="invoice-info-label">INVOICE</p>
              <p v-if="invoiceNo" class="invoice-info-number">{{ invoiceNo }}</p>
              <p v-if="issuedAtLabel" class="invoice-info-date">{{ issuedAtLabel }}</p>
            </div>
          </div>

          <div class="invoice-summary-meta">
            <div class="invoice-billed-to">
              <p class="invoice-meta-label">BILLED TO</p>
              <p class="invoice-billed-name">{{ agencyName || "—" }}</p>
              <p v-if="billingEmailDisplay" class="invoice-billed-email invoice-email-text">{{ billingEmailDisplay }}</p>
              <p v-if="billingAddress" class="invoice-billed-address">{{ billingAddress }}</p>
            </div>
            <div class="invoice-amount-block">
              <p class="invoice-meta-label">{{ isPaid ? "AMOUNT PAID" : "AMOUNT DUE" }}</p>
              <h1 class="invoice-amount-value">{{ formattedPayable }}</h1>
            </div>
          </div>
        </div>

        <div class="invoice-line-items">
          <table class="invoice-items-table">
            <thead>
              <tr>
                <th class="col-id">Order ID</th>
                <th class="col-title">Order Title</th>
                <th class="col-links">Total Links</th>
                <th class="col-amount">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in invoiceRows" :key="'inv-row-' + index">
                <td class="col-id">{{ row.id }}</td>
                <td class="col-title">{{ row.title }}</td>
                <td class="col-links">{{ row.links }}</td>
                <td class="col-amount">{{ formatMoney(row.amount) }}</td>
              </tr>
              <tr v-if="!invoiceRows.length">
                <td class="col-empty" colspan="4">No items</td>
              </tr>
            </tbody>
          </table>
        </div>

          <div class="invoice-totals">
            <div class="invoice-totals-row">
              <span>Subtotal</span>
              <strong>{{ formattedAmount }}</strong>
            </div>
            <div v-if="achDiscountAmount > 0" class="invoice-totals-row is-discount">
              <span>Discount (1%)</span>
              <strong>(-{{ formattedDiscount }})</strong>
            </div>
            <div class="invoice-totals-row is-due">
              <span>{{ isPaid ? "Total paid" : "Total due" }}</span>
              <strong>{{ formattedPayable }}</strong>
            </div>
          </div>
      </aside>

      <section class="invoice-checkout-panel">
        <h2 class="invoice-checkout-title">Secure Checkout</h2>

          <div class="invoice-method-tabs" role="tablist">
            <button
              v-if="canUseCardMethod"
              type="button"
              class="invoice-method-tab"
              :class="{ active: paymentMethod === 'card' }"
              @click="selectPaymentMethod('card')"
            >
              <svg
                v-if="paymentMethod === 'card'"
                class="invoice-card-icon"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.8" />
                <path d="M2 10H22" stroke="currentColor" stroke-width="1.8" />
              </svg>
              Card
            </button>
            <button
              type="button"
              class="invoice-method-tab"
              :class="{ active: paymentMethod === 'paypal' }"
              @click="selectPaymentMethod('paypal')"
            >
              PayPal
            </button>
            <button
              type="button"
              class="invoice-method-tab"
              :class="{ active: paymentMethod === 'ach' }"
              @click="selectPaymentMethod('ach')"
            >
              ACH
            </button>
          </div>

          <form v-if="paymentMethod === 'card'" class="invoice-card-form" @submit.prevent="submitPayment">
            <div class="invoice-billing-heading">Billing Details</div>

            <div class="invoice-field">
              <label for="invoice-address">ADDRESS</label>
              <input
                id="invoice-address"
                v-model="form.address"
                type="text"
                placeholder="Address"
              />
              <div v-if="fieldErrors.address" class="invoice-inline-error">{{ fieldErrors.address }}</div>
            </div>

            <div class="invoice-field-row">
              <div class="invoice-field">
                <label for="invoice-country">COUNTRY</label>
                <select id="invoice-country" v-model="form.country" @change="getStates(form.country)">
                  <option :value="null">Select Country</option>
                  <option
                    v-for="country in countries"
                    :key="country.id"
                    :value="country.id"
                  >
                    {{ country.name }}
                  </option>
                </select>
                <div v-if="fieldErrors.country" class="invoice-inline-error">{{ fieldErrors.country }}</div>
              </div>
              <div class="invoice-field">
                <label for="invoice-state">STATE</label>
                <select id="invoice-state" v-model="form.state">
                  <option :value="null">Select State</option>
                  <option
                    v-for="state in states"
                    :key="state.id"
                    :value="state.id"
                  >
                    {{ state.name }}
                  </option>
                </select>
                <div v-if="fieldErrors.state" class="invoice-inline-error">{{ fieldErrors.state }}</div>
              </div>
            </div>

            <div class="invoice-field-row">
              <div class="invoice-field">
                <label for="invoice-city">CITY</label>
                <input
                  id="invoice-city"
                  v-model="form.city"
                  type="text"
                  placeholder="City"
                />
                <div v-if="fieldErrors.city" class="invoice-inline-error">{{ fieldErrors.city }}</div>
              </div>
              <div class="invoice-field">
                <label for="invoice-zip">ZIP CODE</label>
                <input
                  id="invoice-zip"
                  v-model="form.zipCode"
                  type="text"
                  placeholder="Enter Zip Code"
                />
                <div v-if="fieldErrors.zipCode" class="invoice-inline-error">{{ fieldErrors.zipCode }}</div>
              </div>
            </div>

            <div v-if="canUseSavedCards && canPayWithPersonalCard" class="card-toggle">
              <button
                type="button"
                class="toggle-btn"
                :class="{ active: cardTab === 'new' }"
                @click="showNewCard"
              >
                New Card
              </button>
              <button
                type="button"
                class="toggle-btn"
                :class="{ active: cardTab === 'saved' }"
                @click="showSavedCard"
              >
                Saved Card
              </button>
            </div>

            <template v-if="showNewCardFields">
              <div class="invoice-card-details-header">
                <div class="invoice-billing-heading">Card Details</div>
                <label v-if="canUseSavedCards" class="save-card-checkbox">
                  <input
                    type="checkbox"
                    v-model="saveCheck"
                    true-value="save_card"
                    false-value="no"
                  />
                  <span>Save This Card</span>
                </label>
              </div>

              <div class="invoice-field-row">
                <div class="invoice-field">
                  <label for="invoice-first-name">FIRST NAME</label>
                  <input
                    id="invoice-first-name"
                    v-model="form.first_name"
                    type="text"
                    placeholder="First Name"
                    autocomplete="given-name"
                  />
                  <div v-if="fieldErrors.first_name" class="invoice-inline-error">{{ fieldErrors.first_name }}</div>
                </div>
                <div class="invoice-field">
                  <label for="invoice-last-name">LAST NAME</label>
                  <input
                    id="invoice-last-name"
                    v-model="form.last_name"
                    type="text"
                    placeholder="Last Name"
                    autocomplete="family-name"
                  />
                  <div v-if="fieldErrors.last_name" class="invoice-inline-error">{{ fieldErrors.last_name }}</div>
                </div>
              </div>

              <div class="invoice-field">
                <label>CARD NUMBER</label>
                <div id="invoice-card-element" class="invoice-stripe-element"></div>
                <div id="invoice-card-errors" class="invoice-inline-error"></div>
              </div>
            </template>

            <div v-else-if="showSavedCardFields" class="saved-card-section">
              <div class="saved-card-list">
                <label
                  v-for="card in normalizedSavedCards"
                  :key="card.id"
                  class="saved-card-item"
                  :class="{ active: String(selectedSavedCardId) === String(card.id) }"
                >
                  <input
                    type="radio"
                    name="saved-card"
                    v-model="selectedSavedCardId"
                    :value="card.id"
                    @change="getSelectedCardDetail(card.id)"
                  />
                  <span class="saved-radio">
                    <i class="fas fa-check"></i>
                  </span>
                  <div class="saved-card-body">
                    <img src="@/assets/common/images/chip.svg" alt="Chip" />
                    <div class="saved-card-text">
                      <span class="saved-card-mask">**** **** **** {{ card.last4 || "****" }}</span>
                    </div>
                  </div>
                </label>
              </div>
              <div v-if="!normalizedSavedCards.length" class="invoice-inline-error" style="color:#666;">
                No saved cards found.
              </div>
              <div v-if="fieldErrors.saved_card" class="invoice-inline-error">{{ fieldErrors.saved_card }}</div>
            </div>

            <button type="submit" class="invoice-pay-btn" :disabled="loading || paying">
              Pay {{ formattedAmount }}
              <span class="invoice-pay-arrow">→</span>
            </button>
          </form>

          <div v-else-if="paymentMethod === 'paypal'" class="invoice-paypal-panel">
            <p class="invoice-paypal-copy">
              You will be redirected to PayPal to complete this purchase.
            </p>
            <div id="invoice-paypal-button-container"></div>
          </div>

          <form v-else-if="paymentMethod === 'ach'" class="invoice-card-form" @submit.prevent="submitAchPayment">
            <div class="invoice-billing-heading">Account holder</div>
            <p class="invoice-ach-copy">
              Pay from a US bank account. The transfer usually clears in 2–5 business days. Your order stays on hold until the payment is confirmed.
            </p>

            <div class="invoice-field-row">
              <div class="invoice-field">
                <label for="invoice-ach-first-name">FIRST NAME</label>
                <input
                  id="invoice-ach-first-name"
                  v-model="form.first_name"
                  type="text"
                  placeholder="First Name"
                  autocomplete="given-name"
                />
                <div v-if="fieldErrors.first_name" class="invoice-inline-error">{{ fieldErrors.first_name }}</div>
              </div>
              <div class="invoice-field">
                <label for="invoice-ach-last-name">LAST NAME</label>
                <input
                  id="invoice-ach-last-name"
                  v-model="form.last_name"
                  type="text"
                  placeholder="Last Name"
                  autocomplete="family-name"
                />
                <div v-if="fieldErrors.last_name" class="invoice-inline-error">{{ fieldErrors.last_name }}</div>
              </div>
            </div>

            <div class="invoice-field">
              <label for="invoice-ach-email">EMAIL</label>
              <input
                id="invoice-ach-email"
                v-model="form.email"
                type="email"
                placeholder="Email"
                autocomplete="email"
              />
              <div v-if="fieldErrors.email" class="invoice-inline-error">{{ fieldErrors.email }}</div>
            </div>

            <div class="invoice-billing-heading">Bank account</div>
            <div class="invoice-field">
              <div
                id="invoice-ach-element"
                class="invoice-stripe-element invoice-ach-element"
                :class="{ 'is-loading': achLoading }"
              ></div>
              <p v-if="achLoading" class="invoice-ach-loading">Loading bank checkout…</p>
              <div id="invoice-ach-errors" class="invoice-inline-error">{{ achError }}</div>
            </div>

            <button type="submit" class="invoice-pay-btn" :disabled="loading || paying">
              Pay {{ formattedPayable }} with ACH
              <span class="invoice-pay-arrow">→</span>
            </button>
          </form>

          <p class="invoice-secure-note">
            <svg
              class="invoice-lock-icon"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <rect x="3" y="7" width="10" height="7" rx="1.5" stroke="currentColor" stroke-width="1.4" />
              <path
                d="M5.2 7V5.2C5.2 3.43269 6.63269 2 8.4 2C10.1673 2 11.6 3.43269 11.6 5.2V7"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              />
            </svg>
            Payments are secured with TLS 256-bit encryption
          </p>
      </section>
      </div>
    </div>
  </div>
</template>

<script>
import url, { formatEmailDisplay } from "@/prod.js";
import LoaderComponent from "@/components/LoaderComponent.vue";
import { useToast } from "vue-toastification";
import swal from "sweetalert2";

const toast = useToast();

export default {
  name: "InvoicePaymentPage",
  components: {
    LoaderComponent,
  },
  data() {
    return {
      loading: false,
      pageState: "",
      paymentMethod: "card",
      amountDue: 0,
      isPaid: false,
      invoiceStatus: "open",
      invoiceId: "",
      invoiceNo: "",
      invoiceFilename: "",
      agencyName: "",
      billingEmail: "",
      billingAddress: "",
      issuedAt: "",
      invoiceItems: [],
      /** Payment.vue / Add.php shaped checkouts keyed by draft_order_id (from invoice generate). */
      checkouts: {},
      countries: [],
      states: [],
      stripeKey: url.stripeKey,
      paypal: {
        sandbox: url.paypalKey,
      },
      stripeElement: null,
      stripCard: null,
      stripeScriptPromise: null,
      paypalScriptPromise: null,
      achElements: null,
      achPaymentElement: null,
      achClientSecret: "",
      achError: "",
      achLoading: false,
      achFormComplete: false,
      paying: false,
      cardTab: "new",
      saveCheck: "no",
      existingCard: "no",
      selectedSavedCardId: null,
      existingCardList: [],
      savedCardsAllowed: false,
      paymentMethodId: "",
      form: {
        first_name: "",
        last_name: "",
        email: "",
        address: "",
        country: null,
        state: null,
        city: "",
        zipCode: "",
      },
      fieldErrors: {
        first_name: "",
        last_name: "",
        email: "",
        address: "",
        country: "",
        state: "",
        city: "",
        zipCode: "",
        saved_card: "",
      },
    };
  },
  computed: {
    paymentToken() {
      return String(this.$route.query.data || "").trim();
    },
    checkoutUser() {
      return this.$store?.getters?.["Auth/getUser"] || {};
    },
    canUseSavedCards() {
      if (!this.savedCardsAllowed) return false;
      if (this.checkoutUser.role === "Associate") return Number(this.checkoutUser.is_save_card) === 1;
      return true;
    },
    canPayWithPersonalCard() {
      if (this.checkoutUser.role !== "Associate") return true;
      return Number(this.checkoutUser.is_add_card) === 1;
    },
    canUseCardMethod() {
      return this.canPayWithPersonalCard || this.canUseSavedCards;
    },
    showNewCardFields() {
      if (!this.canPayWithPersonalCard) return false;
      return !this.canUseSavedCards || this.cardTab === "new";
    },
    showSavedCardFields() {
      return this.canUseSavedCards && this.cardTab === "saved";
    },
    normalizedSavedCards() {
      return (this.existingCardList || []).map((card) => ({
        id:
          card.id ||
          card.card_id ||
          card.payment_method_id ||
          card.stripe_payment_method_id ||
          card.last4 ||
          card.last_4,
        last4: card.last4 || card.last_4 || card.card_last4 || "",
        exp_month: card.exp_month || card.expiry_month || "",
        exp_year: card.exp_year || card.expiry_year || "",
      }));
    },
    formattedAmount() {
      return this.formatMoney(this.amountDue);
    },
    achDiscountAmount() {
      if (this.paymentMethod !== "ach") return 0;
      const amount = Number(this.amountDue) || 0;
      return Math.round(amount * 0.01 * 100) / 100;
    },
    payableAmount() {
      const amount = Number(this.amountDue) || 0;
      return Math.round((amount - this.achDiscountAmount) * 100) / 100;
    },
    formattedDiscount() {
      return this.formatMoney(this.achDiscountAmount);
    },
    formattedPayable() {
      return this.formatMoney(this.payableAmount);
    },
    isProcessing() {
      return String(this.invoiceStatus || "").toLowerCase() === "processing";
    },
    isFailed() {
      return String(this.invoiceStatus || "").toLowerCase() === "failed";
    },
    canPayInvoice() {
      const status = String(this.invoiceStatus || "open").toLowerCase();
      return status === "open" || status === "";
    },
    statusBadgeClass() {
      if (this.isPaid) return "is-paid";
      if (this.isProcessing) return "is-processing";
      if (this.isFailed) return "is-failed";
      return "is-open";
    },
    statusBadgeLabel() {
      if (this.isPaid) return "PAID";
      if (this.isProcessing) return "PROCESSING";
      if (this.isFailed) return "FAILED";
      return "OPEN";
    },
    issuedAtLabel() {
      return String(this.issuedAt || "").trim();
    },
    billingEmailDisplay() {
      return formatEmailDisplay(this.billingEmail);
    },
    invoiceRows() {
      return (this.invoiceItems || []).map((item) => {
        const type = item?.type || "";
        let id = "—";
        if (item?.order_id) {
          id = String(item.order_id);
        } else if (item?.draft_order_id) {
          id = String(item.draft_order_id);
        }
        const title =
          String(item?.description || "").trim() ||
          (type === "wallet_top_up" ? "Wallet Top Up" : "Item");
        const qty = Math.max(Number(item?.qty) || 0, 0);

        return {
          id,
          title,
          links: type === "wallet_top_up" ? "—" : String(qty),
          amount: Number(item?.amount) || 0,
        };
      });
    },
    achBillingDetails() {
      return {
        name: `${this.form.first_name || ""} ${this.form.last_name || ""}`.trim(),
        email: String(this.form.email || this.billingEmail || "").trim(),
      };
    },
  },
  watch: {
    "form.first_name"() {
      this.syncAchBillingDetails();
    },
    "form.last_name"() {
      this.syncAchBillingDetails();
    },
    "form.email"() {
      this.syncAchBillingDetails();
    },
  },
  methods: {
    formatMoney(value) {
      return Number(value || 0).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
    },
    buildCheckoutsMap(items) {
      const map = {};
      (items || []).forEach((item) => {
        if (!item || typeof item !== "object") return;
        if (item.type !== "draft_order" || !item.checkout || typeof item.checkout !== "object") {
          return;
        }
        const draftId = Number(item.draft_order_id || item.checkout.draft_order_id || 0);
        if (!draftId) return;
        map[draftId] = {
          ...item.checkout,
          draft_order_id: draftId,
          opportunity_id:
            item.checkout.opportunity_id ?? item.opportunity_id ?? null,
        };
      });
      return map;
    },
    async loadInvoice(options = {}) {
      const preservePaid = !!options.preservePaid;
      if (!this.paymentToken) {
        this.savedCardsAllowed = false;
        this.pageState = "invalid";
        this.loading = false;
        return;
      }

      this.loading = true;
      try {
        const res = await this.$store.dispatch(
          "Product/getInvoicePaymentAction",
          this.paymentToken
        );
        const invoice = res?.invoice;
        if (!res?.success || !invoice) {
          this.savedCardsAllowed = false;
          if (preservePaid) {
            this.isPaid = true;
            this.invoiceStatus = "paid";
            this.pageState = "ready";
            return;
          }
          this.pageState = "invalid";
          return;
        }

        this.amountDue = Number(invoice.amount) || 0;
        this.invoiceStatus = String(invoice.status || "").toLowerCase();
        this.isPaid =
          !!invoice.is_paid || this.invoiceStatus === "paid" || preservePaid;
        this.invoiceId = invoice.id || "";
        this.invoiceNo = invoice.invoice_no || "";
        this.invoiceFilename = invoice.filename || ((this.invoiceNo || ("INV-" + (invoice.id || ""))) + ".pdf");
        this.agencyName = invoice.agency_name || "";
        this.billingEmail = formatEmailDisplay(invoice.billing_email);
        this.billingAddress = String(invoice.billing_address || "").trim();
        if (!String(this.form.email || "").trim()) {
          this.form.email = this.billingEmail || "";
        }
        this.prefillAccountHolderName();
        this.issuedAt = invoice.issued_at || "";
        this.invoiceItems = Array.isArray(invoice.items) ? invoice.items : [];
        this.checkouts = this.buildCheckoutsMap(this.invoiceItems);
        this.savedCardsAllowed = !!invoice.can_use_saved_cards;
        if (this.paymentMethod === "card" && !this.canUseCardMethod) {
          this.paymentMethod = "paypal";
        } else if (this.paymentMethod === "card" && !this.canPayWithPersonalCard && this.canUseSavedCards) {
          this.cardTab = "saved";
        }
        this.pageState = "ready";

        if (this.canPayInvoice) {
          this.loadStripeScript();
          this.loadPaypalScript();
          await this.getCountries();
          await this.getExistingCards();
          await this.$nextTick();
          if (this.showNewCardFields) {
            await this.initializeStripe();
          } else {
            await this.ensureStripeInstance();
          }
          await this.handleAchRedirectReturn();
        }
      } catch (error) {
        this.savedCardsAllowed = false;
        if (preservePaid) {
          this.isPaid = true;
          this.invoiceStatus = "paid";
          this.pageState = "ready";
          return;
        }
        this.pageState = "invalid";
      } finally {
        this.loading = false;
      }
    },
    async getCountries() {
      try {
        const res = await this.$store.dispatch("Product/getInvoicePaymentCountriesAction", this.paymentToken);
        this.countries = res?.countries || [];
      } catch {
        this.countries = [];
      }
    },
    async getStates(countryId) {
      this.form.state = null;
      this.states = [];
      if (!countryId) return;
      try {
        const res = await this.$store.dispatch("Product/getInvoicePaymentStatesAction", {
          countryId,
          token: this.paymentToken,
        });
        this.states = res?.states || [];
      } catch {
        this.states = [];
      }
    },
    splitPersonName(value) {
      const name = String(value || "").trim().replace(/\s+/g, " ");
      if (!name) return { first: "", last: "" };
      const space = name.indexOf(" ");
      if (space === -1) return { first: name, last: "" };
      return {
        first: name.slice(0, space),
        last: name.slice(space + 1).trim(),
      };
    },
    prefillAccountHolderName() {
      const user = this.$store?.getters?.["Auth/getUser"] || {};
      const parts = this.splitPersonName(user.name || this.agencyName);
      if (!String(this.form.first_name || "").trim() && parts.first) {
        this.form.first_name = parts.first;
      }
      if (!String(this.form.last_name || "").trim() && parts.last) {
        this.form.last_name = parts.last;
      }
    },
    resetFieldErrors() {
      this.fieldErrors = {
        first_name: "",
        last_name: "",
        email: "",
        address: "",
        country: "",
        state: "",
        city: "",
        zipCode: "",
        saved_card: "",
      };
    },
    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
    },
    syncAchBillingDetails() {
      if (!this.achPaymentElement) return;
      try {
        this.achPaymentElement.update({
          defaultValues: {
            billingDetails: this.achBillingDetails,
          },
        });
      } catch {
        // Stripe element may already be unmounted
      }
    },
    friendlyAchError(error) {
      const message = String(error?.message || error || "").trim();
      const code = String(error?.code || "").toLowerCase();
      const lower = message.toLowerCase();
      if (
        lower.includes("confirmparams") ||
        lower.includes("payment_method_data") ||
        lower.includes("fields.billing") ||
        lower.includes("you specified") ||
        lower.includes("opt out of collecting")
      ) {
        return "Please confirm your bank details and try again.";
      }
      if (
        code === "incomplete" ||
        lower.includes("please select a payment method") ||
        lower.includes("canceled") ||
        lower.includes("cancelled")
      ) {
        return "Please confirm your bank details to continue.";
      }
      return message || "ACH payment failed.";
    },
    async remountAchElement() {
      this.teardownAchElement();
      await this.initializeAchElement();
    },
    isAchPaymentConfirmed(paymentIntent) {
      const status = String(paymentIntent?.status || "").toLowerCase();
      if (status !== "processing" && status !== "succeeded") {
        return false;
      }
      if (paymentIntent?.next_action) {
        return false;
      }
      return true;
    },
    async getConfirmedAchIntent(paymentIntent) {
      try {
        if (!this.stripeElement || !this.achClientSecret) {
          return paymentIntent;
        }
        const { paymentIntent: freshIntent } = await this.stripeElement.retrievePaymentIntent(
          this.achClientSecret
        );
        return freshIntent || paymentIntent;
      } catch {
        return paymentIntent;
      }
    },
    validateBillingFields() {
      this.resetFieldErrors();
      let valid = true;
      if (!String(this.form.address || "").trim()) {
        this.fieldErrors.address = "Address is required.";
        valid = false;
      }
      if (!this.form.country) {
        this.fieldErrors.country = "Country is required.";
        valid = false;
      }
      if (!this.form.state) {
        this.fieldErrors.state = "State is required.";
        valid = false;
      }
      if (!String(this.form.city || "").trim()) {
        this.fieldErrors.city = "City is required.";
        valid = false;
      }
      if (!String(this.form.zipCode || "").trim()) {
        this.fieldErrors.zipCode = "Zip code is required.";
        valid = false;
      }
      return valid;
    },
    getDraftValidationError(source) {
      return (
        source?.response?.data?.message ||
        source?.response?.data?.error ||
        source?.message ||
        source?.error ||
        "This order does not exist"
      );
    },
    extractInvalidDraftOrderIds(source) {
      const data = source?.response?.data || source || {};
      const idFields = [
        "invalid_draft_order_ids",
        "missing_draft_order_ids",
        "removed_draft_order_ids",
      ];

      for (const field of idFields) {
        const value = data[field];
        if (Array.isArray(value) && value.length) {
          return [...new Set(value.map((id) => String(id).trim()).filter(Boolean))];
        }
      }

      const singleId = data.invalid_draft_order_id || data.draft_order_id;
      if (singleId) {
        return [String(singleId).trim()];
      }

      const msg = String(data.message || data.error || "");
      const parsedFromMessage = [...msg.matchAll(/#(\d+)/g)].map((match) => match[1]);
      if (parsedFromMessage.length) {
        return [...new Set(parsedFromMessage)];
      }

      return [];
    },
    isDraftOrderExistenceError(source) {
      const data = source?.response?.data || (source?.success === false ? source : null);
      if (data?.success === false) {
        return true;
      }

      const rawMsg =
        source?.response?.data?.message ||
        source?.response?.data?.error ||
        source?.message ||
        source?.error ||
        "";
      const msg = String(rawMsg).toLowerCase();
      return (
        msg.includes("does not exist") ||
        msg.includes("already paid") ||
        msg.includes("removed") ||
        msg.includes("no longer available")
      );
    },
    buildDraftOrderExistenceErrorMessage(source) {
      const ids = this.extractInvalidDraftOrderIds(source);
      if (ids.length) {
        return `ID # ${ids.join(",")} is already paid or removed`;
      }
      return "This order is already paid or removed";
    },
    showDraftOrderExistenceErrorPopup(source) {
      return swal.fire({
        icon: "error",
        title: "Unable to proceed",
        text: this.buildDraftOrderExistenceErrorMessage(source),
        confirmButtonText: "OK",
      });
    },
    handleDraftValidationFailure(source) {
      if (this.isDraftOrderExistenceError(source)) {
        this.showDraftOrderExistenceErrorPopup(source);
        return;
      }
      const msg =
        source?.response?.data?.message ||
        source?.response?.data?.error ||
        source?.message ||
        source?.error ||
        "Unable to validate draft orders.";
      toast.error(msg, { timeout: 4000 });
    },
    async validateInvoiceDraftOrders() {
      if (!this.paymentToken) {
        toast.error("This payment link is invalid.", { timeout: 4000 });
        return false;
      }

      try {
        const res = await this.$store.dispatch(
          "Product/validateInvoiceDraftOrdersAction",
          this.paymentToken
        );
        if (!res?.success) {
          this.handleDraftValidationFailure(res);
          return false;
        }
        return true;
      } catch (error) {
        this.handleDraftValidationFailure(error);
        return false;
      }
    },
    selectPaymentMethod(method) {
      if (method === "card" && !this.canUseCardMethod) return;
      this.paymentMethod = method;
      this.achError = "";
      this.$nextTick(() => {
        if (method === "card") {
          this.teardownAchElement();
          if (this.showNewCardFields) {
            this.initializeStripe();
          } else {
            this.ensureStripeInstance();
          }
        } else if (method === "paypal") {
          this.teardownAchElement();
          this.paypalOption();
        } else if (method === "ach") {
          this.prefillAccountHolderName();
          this.unmountStripeCard();
          this.initializeAchElement();
        }
      });
    },
    loadStripeScript() {
      if (this.stripeScriptPromise) {
        return this.stripeScriptPromise;
      }
      this.stripeScriptPromise = new Promise((resolve, reject) => {
        const existing = document.getElementById("stripe-js");
        if (existing) {
          if (window.Stripe) {
            resolve(window.Stripe);
            return;
          }
          existing.addEventListener("load", () => resolve(window.Stripe), { once: true });
          existing.addEventListener("error", () => reject(new Error("Failed to load Stripe script")), { once: true });
          return;
        }
        const script = document.createElement("script");
        script.id = "stripe-js";
        script.src = "https://js.stripe.com/v3/";
        script.onload = () => resolve(window.Stripe);
        script.onerror = () => reject(new Error("Failed to load Stripe script"));
        document.head.appendChild(script);
      });
      return this.stripeScriptPromise;
    },
    async initializeStripe() {
      try {
        const ready = await this.ensureStripeInstance();
        if (!ready) return;
        const host = document.getElementById("invoice-card-element");
        if (!host) return;

        this.unmountStripeCard();

        host.innerHTML = "";
        const elements = this.stripeElement.elements();
        this.stripCard = elements.create("card", {
          style: {
            base: {
              fontSize: "14px",
              color: "#0f172a",
              fontFamily: "Inter, sans-serif",
              "::placeholder": { color: "#94a3b8" },
            },
          },
          hidePostalCode: true,
        });
        this.stripCard.mount("#invoice-card-element");
        this.stripCard.on("change", (event) => {
          const displayError = document.getElementById("invoice-card-errors");
          if (displayError) displayError.textContent = event.error ? event.error.message : "";
        });
      } catch {
        // ignore mount failures
      }
    },
    async ensureStripeInstance() {
      try {
        const StripeConstructor = await this.loadStripeScript();
        if (!this.stripeElement) {
          this.stripeElement = StripeConstructor(this.stripeKey);
        }
        return !!this.stripeElement;
      } catch {
        return false;
      }
    },
    unmountStripeCard() {
      if (this.stripCard) {
        try {
          this.stripCard.unmount();
        } catch {
          // already unmounted
        }
        this.stripCard = null;
      }
    },
    showNewCard() {
      this.cardTab = "new";
      this.existingCard = "no";
      this.fieldErrors.saved_card = "";
      this.$nextTick(() => {
        this.initializeStripe();
      });
    },
    showSavedCard() {
      if (!this.canUseSavedCards) return;
      this.cardTab = "saved";
      this.existingCard = "yes";
      this.saveCheck = "no";
      this.unmountStripeCard();
      if (this.selectedSavedCardId) {
        this.getSelectedCardDetail(this.selectedSavedCardId);
      }
    },
    async getExistingCards() {
      if (!this.paymentToken || !this.canUseSavedCards) {
        this.existingCardList = [];
        this.cardTab = "new";
        this.existingCard = "no";
        return;
      }
      try {
        const res = await this.$store.dispatch(
          "Product/getInvoiceExistingCardsAction",
          this.paymentToken
        );
        let cards = [];
        if (Array.isArray(res)) {
          cards = res;
        } else if (Array.isArray(res?.cards)) {
          cards = res.cards;
        } else if (Array.isArray(res?.data)) {
          cards = res.data;
        } else if (res && typeof res === "object") {
          cards = Object.values(res).filter((v) => v && typeof v === "object");
        }
        this.existingCardList = cards;
        if (this.existingCardList.length > 0 && this.paymentMethod === "card") {
          this.selectedSavedCardId = null;
          this.showSavedCard();
        } else {
          this.cardTab = "new";
          this.existingCard = "no";
        }
      } catch {
        this.existingCardList = [];
        this.cardTab = "new";
        this.existingCard = "no";
      }
    },
    async getSelectedCardDetail(id) {
      if (!id || !this.paymentToken) return;
      this.selectedSavedCardId = id;
      this.loading = true;
      try {
        const res = await this.$store.dispatch("Product/getInvoiceCardDetailAction", {
          id,
          token: this.paymentToken,
        });
        this.form.address = res?.line1 || "";
        this.form.country = res?.country || null;
        if (this.form.country) {
          await this.getStates(this.form.country);
        }
        this.form.state = res?.state || null;
        this.form.city = res?.city || "";
        this.form.zipCode = res?.postal_code || "";
      } catch {
        // keep user entered values as is
      } finally {
        this.loading = false;
      }
    },
    async ensureStripeReady() {
      if (!this.stripCard || !this.stripeElement) {
        await this.$nextTick();
        await this.initializeStripe();
      }
      return !!(this.stripCard && this.stripeElement);
    },
    teardownAchElement() {
      if (this.achPaymentElement) {
        try {
          this.achPaymentElement.unmount();
        } catch {
          // already unmounted
        }
        this.achPaymentElement = null;
      }
      this.achElements = null;
      this.achFormComplete = false;
    },
    async initializeAchElement() {
      this.achError = "";
      if (this.achPaymentElement && this.achElements && this.achClientSecret) {
        return;
      }
      this.achLoading = true;
      try {
        if (!this.stripeElement) {
          const StripeConstructor = await this.loadStripeScript();
          this.stripeElement = StripeConstructor(this.stripeKey);
        }
        if (!this.achClientSecret) {
          const intent = await this.$store.dispatch(
            "Product/invoiceAchPaymentIntentAction",
            this.billingPayload({
              first_name: this.form.first_name || "Account",
              last_name: this.form.last_name || "Holder",
              email: String(this.form.email || "").trim(),
            }),
            { root: true }
          );
          this.achClientSecret = intent?.client_secret || "";
        }
        if (!this.achClientSecret) {
          this.achError = "Unable to start ACH checkout.";
          return;
        }
        await this.$nextTick();
        const host = document.getElementById("invoice-ach-element");
        if (!host || this.paymentMethod !== "ach") return;

        this.teardownAchElement();
        host.innerHTML = "";
        this.achElements = this.stripeElement.elements({
          clientSecret: this.achClientSecret,
          appearance: {
            theme: "stripe",
            variables: {
              fontFamily: "Inter, sans-serif",
              fontSizeBase: "14px",
              colorPrimary: "#14b8c1",
            },
          },
        });
        this.achPaymentElement = this.achElements.create("payment", {
          layout: "tabs",
          wallets: {
            applePay: "never",
            googlePay: "never",
          },
          defaultValues: {
            billingDetails: this.achBillingDetails,
          },
          fields: {
            billingDetails: {
              name: "never",
              email: "never",
            },
          },
        });
        this.achPaymentElement.mount("#invoice-ach-element");
        this.achPaymentElement.on("ready", () => {
          this.achLoading = false;
          this.syncAchBillingDetails();
        });
        this.achPaymentElement.on("change", (event) => {
          this.achFormComplete = !!event?.complete;
          if (event?.error) {
            this.achError = this.friendlyAchError(event.error);
          } else if (this.achFormComplete) {
            this.achError = "";
          }
        });
        this.achPaymentElement.on("loaderror", (event) => {
          this.achLoading = false;
          this.achError = this.friendlyAchError(event?.error) || "Unable to load bank checkout.";
        });
      } catch (e) {
        this.achLoading = false;
        this.achError =
          this.friendlyAchError(
            e?.response?.data?.message ||
              (e?.response?.data?.errors && Object.values(e.response.data.errors).flat()[0]) ||
              e?.message
          ) || "Unable to load ACH checkout.";
      }
    },
    async submitAchPayment() {
      if (this.paying || this.loading) return;
      this.paying = true;
      await this.$nextTick();
      this.resetFieldErrors();
      this.achError = "";
      if (!String(this.form.first_name || "").trim()) {
        this.fieldErrors.first_name = "First name is required.";
      }
      if (!String(this.form.last_name || "").trim()) {
        this.fieldErrors.last_name = "Last name is required.";
      }
      const achEmail = String(this.form.email || "").trim();
      if (!achEmail) {
        this.fieldErrors.email = "Email is required.";
      } else if (!this.isValidEmail(achEmail)) {
        this.fieldErrors.email = "Please enter a valid email.";
      }
      if (this.fieldErrors.first_name || this.fieldErrors.last_name || this.fieldErrors.email) {
        this.paying = false;
        toast.error("Please enter the account holder first name, last name, and email.", { timeout: 3000 });
        return;
      }
      if (!this.achFormComplete) {
        this.paying = false;
        this.achError = "Please confirm your bank details to continue.";
        toast.error(this.achError, { timeout: 4000 });
        return;
      }
      this.loading = true;
      await this.$nextTick();
      if (!(await this.validateInvoiceDraftOrders())) {
        this.paying = false;
        this.loading = false;
        return;
      }

      try {
        if (!this.achElements || !this.achClientSecret) {
          await this.initializeAchElement();
          if (!this.achElements || !this.achClientSecret) {
            toast.error(this.achError || "ACH form is not ready yet.", { timeout: 4000 });
            return;
          }
        }

        this.syncAchBillingDetails();
        await this.$nextTick();

        const billingDetails = this.achBillingDetails;
        const { error, paymentIntent } = await this.stripeElement.confirmPayment({
          elements: this.achElements,
          confirmParams: {
            return_url: window.location.href,
            payment_method_data: {
              billing_details: {
                name: billingDetails.name,
                email: billingDetails.email,
              },
            },
          },
          redirect: "if_required",
        });

        if (error) {
          this.achError = this.friendlyAchError(error);
          toast.error(this.achError, { timeout: 4000 });
          await this.remountAchElement();
          return;
        }

        const confirmedIntent = await this.getConfirmedAchIntent(paymentIntent);
        if (!this.isAchPaymentConfirmed(confirmedIntent)) {
          this.achError = "Please confirm your bank details to continue.";
          toast.error(this.achError, { timeout: 4000 });
          await this.remountAchElement();
          return;
        }

        await this.finalizeAchPayment(confirmedIntent);
      } catch (e) {
        const msg = this.friendlyAchError(
          e?.response?.data?.message ||
            (e?.response?.data?.errors && Object.values(e.response.data.errors).flat()[0]) ||
            e?.message
        );
        this.achError = msg;
        toast.error(msg, { timeout: 4000 });
        await this.remountAchElement();
      } finally {
        this.loading = false;
        this.paying = false;
      }
    },
    async finalizeAchPayment(paymentIntent) {
      if (!this.isAchPaymentConfirmed(paymentIntent)) {
        toast.error("Please confirm your bank details to continue.", { timeout: 4000 });
        return false;
      }

      const last4 = paymentIntent?.payment_method?.us_bank_account?.last4 || "";
      const res = await this.$store.dispatch(
        "Product/invoiceAchStartAction",
        this.billingPayload({
          method: "ach",
          paymentIntentId: paymentIntent.id,
          last4,
          email: String(this.form.email || "").trim(),
        }),
        { root: true }
      );

      if (!res?.success) {
        throw new Error(res?.message || "Unable to start ACH payment.");
      }

      this.invoiceStatus = String(res.status_label || "processing").toLowerCase();
      this.isPaid = this.invoiceStatus === "paid";
      if (this.isPaid) {
        toast.success("Invoice successfully paid", { timeout: 3000 });
      } else {
        toast.success("ACH payment started. Your order is on hold until the bank transfer clears.", {
          timeout: 5000,
        });
      }
      await this.loadInvoice();
    },
    async handleAchRedirectReturn() {
      const clientSecret = String(this.$route.query.payment_intent_client_secret || "").trim();
      if (!clientSecret) return;

      try {
        if (!this.stripeElement) {
          const StripeConstructor = await this.loadStripeScript();
          this.stripeElement = StripeConstructor(this.stripeKey);
        }
        const { paymentIntent, error } = await this.stripeElement.retrievePaymentIntent(clientSecret);
        if (error || !paymentIntent) return;
        this.loading = true;
        await this.finalizeAchPayment(paymentIntent);
      } catch {
        // ignore return handling; user can stay on checkout if still open
      } finally {
        this.loading = false;
      }
    },
    loadPaypalScript() {
      if (this.paypalScriptPromise) {
        return this.paypalScriptPromise;
      }
      this.paypalScriptPromise = new Promise((resolve, reject) => {
        if (window.paypal) {
          resolve(window.paypal);
          return;
        }
        const existing = document.querySelector('script[src*="paypal.com/sdk/js"]');
        if (existing) {
          existing.addEventListener("load", () => resolve(window.paypal), { once: true });
          existing.addEventListener("error", () => reject(new Error("Failed to load PayPal")), { once: true });
          return;
        }
        const paypalScript = document.createElement("script");
        paypalScript.src =
          "https://www.paypal.com/sdk/js?client-id=" +
          this.paypal.sandbox +
          "&currency=USD&disable-funding=credit,card,sepa";
        paypalScript.onload = () => resolve(window.paypal);
        paypalScript.onerror = () => reject(new Error("Failed to load PayPal"));
        document.head.appendChild(paypalScript);
      });
      return this.paypalScriptPromise;
    },
    async paypalOption() {
      try {
        await this.loadPaypalScript();
      } catch {
        toast.error("PayPal could not be loaded. Please try again.", { timeout: 4000 });
        return;
      }
      if (!window.paypal) return;

      const container = document.getElementById("invoice-paypal-button-container");
      if (!container) return;
      container.innerHTML = "";

      const vm = this;
      window.paypal
        .Buttons({
          style: {
            shape: "rect",
            color: "gold",
            layout: "vertical",
            label: "paypal",
            size: "responsive",
            tagline: "false",
          },
          createOrder: async (_data, actions) => {
            const valid = await vm.validateInvoiceDraftOrders();
            if (!valid) {
              throw new Error(vm.buildDraftOrderExistenceErrorMessage({ success: false }));
            }
            return actions.order.create({
              purchase_units: [
                {
                  amount: {
                    value: Number(vm.amountDue).toFixed(2),
                  },
                },
              ],
            });
          },
          onApprove: (data, actions) => {
            return actions.order.capture().then(async (details) => {
              try {
                vm.loading = true;
                await vm.confirmInvoicePayment({
                  data: vm.paymentToken,
                  method: "paypal",
                  payment_id: details?.id || data?.orderID,
                  checkouts: vm.checkouts,
                });
              } catch (e) {
                toast.error(
                  e?.response?.data?.message || e.message || "PayPal payment failed.",
                  { timeout: 4000 }
                );
              } finally {
                vm.loading = false;
              }
            });
          },
          onCancel: () => {
            toast.error("PayPal payment cancelled.", { timeout: 3000 });
          },
          onError: (err) => {
            const msg = String(err?.message || "");
            if (
              msg.includes("This order does not exist") ||
              msg.includes("already paid or removed")
            ) {
              return;
            }
            toast.error("PayPal payment failed. Please try again.", { timeout: 4000 });
          },
        })
        .render("#invoice-paypal-button-container");
    },
    async submitPayment() {
      if (this.paying || this.loading) return;
      this.paying = true;
      await this.$nextTick();
      this.resetFieldErrors();
      if (!this.validateBillingFields()) {
        this.paying = false;
        toast.error("Please fill all billing address fields.", { timeout: 3000 });
        return;
      }

      if (this.showSavedCardFields) {
        if (!this.selectedSavedCardId) {
          this.fieldErrors.saved_card = "Please select a card.";
          this.paying = false;
          toast.error("Please select a card.", { timeout: 3000 });
          return;
        }
        this.loading = true;
        await this.$nextTick();
        if (!(await this.validateInvoiceDraftOrders())) {
          this.paying = false;
          this.loading = false;
          return;
        }
        try {
          await this.chargeSavedCard();
        } finally {
          this.paying = false;
        }
        return;
      }

      if (!String(this.form.first_name || "").trim()) {
        this.fieldErrors.first_name = "First name is required.";
      }
      if (!String(this.form.last_name || "").trim()) {
        this.fieldErrors.last_name = "Last name is required.";
      }
      if (this.fieldErrors.first_name || this.fieldErrors.last_name) {
        this.paying = false;
        toast.error("Please enter card holder first and last name.", { timeout: 3000 });
        return;
      }
      this.loading = true;
      await this.$nextTick();
      if (!(await this.validateInvoiceDraftOrders())) {
        this.paying = false;
        this.loading = false;
        return;
      }
      try {
        await this.stripePaymentMethod();
      } finally {
        this.paying = false;
      }
    },
    billingPayload(extra = {}) {
      const payload = {
        data: this.paymentToken,
        address: this.form.address,
        country: this.form.country,
        city: this.form.city,
        state: this.form.state,
        zip_code: this.form.zipCode,
        first_name: this.form.first_name,
        last_name: this.form.last_name,
        // Same Add.php-shaped checkout objects saved when admin generated the invoice.
        checkouts: this.checkouts,
        ...extra,
      };

      if (this.canUseSavedCards && this.paymentMethod === "card") {
        payload.saveCard =
          this.showNewCardFields && this.saveCheck === "save_card" ? "save_card" : "no";
        payload.existingCard = this.showSavedCardFields ? "yes" : "no";
        payload.card = this.showSavedCardFields ? this.selectedSavedCardId || "" : "";
        if (this.showSavedCardFields) {
          payload.method = "save_card";
        }
        if (payload.saveCard === "save_card" && this.paymentMethodId) {
          payload.paymentMethodId = this.paymentMethodId;
        }
      }

      return payload;
    },
    async stripePaymentMethod() {
      const ready = await this.ensureStripeReady();
      if (!ready) {
        toast.error("Card form is not ready yet.", { timeout: 3000 });
        return;
      }
      this.loading = true;
      const cardholderName = `${this.form.first_name || ""} ${this.form.last_name || ""}`.trim();
      const { paymentMethod, error } = await this.stripeElement.createPaymentMethod({
        type: "card",
        card: this.stripCard,
        billing_details: {
          name: cardholderName,
          address: {
            line1: this.form.address,
            city: this.form.city,
            state: this.form.state,
            postal_code: this.form.zipCode,
          },
        },
      });
      if (error) {
        this.loading = false;
        const displayError = document.getElementById("invoice-card-errors");
        if (displayError) displayError.textContent = error.message;
        toast.error(error.message, { timeout: 4000 });
        return;
      }
      try {
        this.paymentMethodId = paymentMethod.id;
        const responseData = await this.$store.dispatch(
          "Product/invoiceStripePaymentMethodAction",
          this.billingPayload({
            payment_method: paymentMethod.id,
            last4: paymentMethod.card.last4,
            name: cardholderName,
          }),
          { root: true }
        );
        await this.handlePayment(responseData, paymentMethod.card.last4);
      } catch (e) {
        this.loading = false;
        const msg =
          e?.response?.data?.message ||
          (e?.response?.data?.errors && Object.values(e.response.data.errors).flat()[0]) ||
          "Payment failed.";
        toast.error(msg, { timeout: 4000 });
      }
    },
    async chargeSavedCard() {
      const ready = await this.ensureStripeInstance();
      if (!ready) {
        toast.error("Payment is not ready yet.", { timeout: 4000 });
        return;
      }

      const selectedCard = this.normalizedSavedCards.find(
        (card) => String(card.id) === String(this.selectedSavedCardId)
      );
      const last4 = selectedCard?.last4 || "";

      this.loading = true;
      try {
        const responseData = await this.$store.dispatch(
          "Product/invoiceStripeSavedPaymentMethodAction",
          this.billingPayload({
            card: this.selectedSavedCardId,
          }),
          { root: true }
        );
        this.paymentMethodId = responseData?.payment_method || "";
        await this.handlePayment(responseData, last4);
      } catch (e) {
        this.loading = false;
        this.fieldErrors.saved_card = "Unable to charge selected saved card.";
        const msg =
          e?.response?.data?.message ||
          (e?.response?.data?.errors && Object.values(e.response.data.errors).flat()[0]) ||
          "Payment failed.";
        toast.error(msg, { timeout: 4000 });
      }
    },
    async confirmInvoicePayment(payload) {
      const itemIndexes = (this.invoiceItems || [])
        .map((item, index) => (item && typeof item === "object" ? index : null))
        .filter((index) => index !== null);
      const indexesToFulfill = itemIndexes.length ? itemIndexes : [null];

      let lastRes = null;
      for (const itemIndex of indexesToFulfill) {
        const chunkPayload = { ...payload };
        if (itemIndex !== null && itemIndex !== undefined) {
          chunkPayload.item_index = itemIndex;
        }

        lastRes = await this.$store.dispatch("Product/invoicePayAction", chunkPayload, {
          root: true,
        });
        if (!lastRes?.success) {
          throw new Error(lastRes?.message || "Unable to finalize payment.");
        }
      }

      this.isPaid = true;
      this.invoiceStatus = "paid";
      toast.success("Invoice successfully paid", { timeout: 3000 });
      await this.loadInvoice();
      this.isPaid = true;
      this.invoiceStatus = "paid";
      this.pageState = "ready";
      return lastRes;
    },
    async downloadInvoice() {
      if (!this.paymentToken) {
        toast.error("Invoice could not be downloaded.", { timeout: 3000 });
        return;
      }
      this.loading = true;
      try {
        const response = await this.$store.dispatch(
          "Product/downloadInvoicePaymentAction",
          this.paymentToken
        );
        if (response && response.type && response.type.indexOf("application/json") !== -1) {
          toast.error("Invoice PDF could not be downloaded.", { timeout: 3000 });
          return;
        }
        const blob = new Blob([response], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = this.invoiceFilename || ((this.invoiceNo || ("INV-" + this.invoiceId)) + ".pdf");
        link.click();
        window.URL.revokeObjectURL(link.href);
      } catch (error) {
        toast.error("Invoice PDF could not be downloaded.", { timeout: 3000 });
      } finally {
        this.loading = false;
      }
    },
    async handlePayment(paymentIntent, last4 = "") {
      try {
        let intent = paymentIntent;
        if (
          paymentIntent?.status === "requires_action" ||
          paymentIntent?.status === "requires_source_action"
        ) {
          const { error, paymentIntent: confirmed } =
            await this.stripeElement.confirmCardPayment(paymentIntent.client_secret);
          if (error) {
            this.loading = false;
            toast.error(error.message, { timeout: 4000 });
            return;
          }
          intent = confirmed;
        }

        if (intent?.status === "succeeded") {
          await this.confirmInvoicePayment(
            this.billingPayload({
              method: "stripe",
              paymentIntentId: intent.id,
              last4,
            })
          );
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || e.message || "Payment failed.", {
          timeout: 4000,
        });
      } finally {
        this.loading = false;
      }
    },
  },
  async mounted() {
    await this.loadInvoice();
  },
  beforeUnmount() {
    this.unmountStripeCard();
    this.teardownAchElement();
  },
};
</script>
